import { PageHero } from '@components/common';
import { BarChart3, Brain, ArrowRight, CheckCircle, Server, Building2, Users } from 'lucide-react';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';

const packages = [
  {
    id: 'security-maturity-assessment',
    icon: BarChart3,
    title: 'Security Maturity Assessment',
    description:
      "A comprehensive evaluation of your organisation's security posture against industry frameworks. Get a clear understanding of where you stand and a prioritised roadmap for improvement.",
    duration: '2-3 weeks',
    highlights: [
      'Current state assessment against NIST or ISO 27001',
      'Detailed gap analysis',
      'Prioritised remediation roadmap',
      'Executive presentation and board report',
    ],
  },
  {
    id: 'ai-governance-setup',
    icon: Brain,
    title: 'AI Governance Setup',
    description:
      'Establish a practical AI governance framework that enables safe AI adoption while managing risks. From policy creation to implementation guidance, we help you harness AI responsibly.',
    duration: '3-4 weeks',
    highlights: [
      'AI governance policy and acceptable use guidelines',
      'AI risk assessment framework',
      'Vendor evaluation criteria',
      'Employee training materials',
    ],
  },
  {
    id: 'technical-estate-review',
    icon: Server,
    title: 'Technical Estate Review',
    description:
      'A thorough assessment of your cloud and on-premise infrastructure security. Identify vulnerabilities, misconfigurations, and technical debt before attackers do.',
    duration: '2-4 weeks',
    highlights: [
      'Cloud security posture assessment (AWS, Azure, GCP)',
      'Network architecture and segmentation review',
      'Identity and access management evaluation',
      'Prioritised technical remediation plan',
    ],
  },
  {
    id: 'ma-cyber-due-diligence',
    icon: Building2,
    title: 'M&A Cyber Due Diligence',
    description:
      'Pre-acquisition security assessment to identify risks and liabilities. Protect your investment and plan integration before you sign.',
    duration: '1-2 weeks',
    highlights: [
      'Target company security posture evaluation',
      'Cyber risk and liability identification',
      'Integration risk assessment',
      'Post-acquisition security roadmap',
    ],
  },
  {
    id: 'board-tabletop-exercises',
    icon: Users,
    title: 'Board Tabletop Exercises',
    description:
      'Interactive incident response exercises for senior leadership. Practice decision-making under pressure and validate your response plans in a safe environment.',
    duration: 'Half day',
    highlights: [
      'NCSC-aligned exercise scenarios',
      'Board and executive level facilitation',
      'Real-time decision-making practice',
      'Actionable improvement recommendations',
    ],
  },
];

export default function PackagesPillarPage() {
  // Service structured data for SEO
  const packagesStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: packages.map((pkg, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Service',
        name: pkg.title,
        description: pkg.description,
        url: `https://thecisonetwork.com/packages/${pkg.id}`,
        provider: {
          '@type': 'Organization',
          name: 'The CISO Network',
          url: 'https://thecisonetwork.com',
        },
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(packagesStructuredData) }}
      />
      <PageHero
        title="Fixed-Price Security Packages"
        subtitle="Repeatable engagements with clear deliverables and predictable timelines. No scope creep, no hourly billing surprises - just results."
        eyebrow="Our Packages"
        seoTitle="Security Packages | The CISO Network"
        seoDescription="Fixed-price security packages including Security Maturity Assessment, AI Governance, Technical Estate Review, M&A Due Diligence, and Board Tabletop Exercises. Clear deliverables, defined timelines."
      />

      {/* Introduction */}
      <section className="py-16 bg-white border-b border-border">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-muted-foreground">
              Sometimes you need a defined engagement with clear outcomes rather than ongoing
              advisory support. Our packages deliver specific, valuable outputs on a predictable
              timeline and budget.
            </p>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8">
            {packages.map(pkg => (
              <Link
                key={pkg.id}
                to={`/packages/${pkg.id}`}
                className="group bg-white rounded-2xl p-8 border border-border hover:border-teal/30 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                <div className="h-14 w-14 rounded-xl bg-teal flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <pkg.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-3 group-hover:text-teal transition-colors">
                  {pkg.title}
                </h2>
                <p className="text-muted-foreground mb-6 flex-grow">{pkg.description}</p>

                <div className="flex items-center gap-4 mb-6 text-sm">
                  <span className="px-3 py-1 bg-background rounded-full text-muted-foreground">
                    {pkg.duration}
                  </span>
                </div>

                <ul className="space-y-2 mb-8">
                  {pkg.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex items-center text-teal font-medium">
                  View full details
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Fixed Price */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Fixed-Price Packages?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Traditional consulting engagements can be unpredictable. Our packages are different.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Predictable Investment',
                description:
                  "Know exactly what you're paying upfront. No surprise invoices or scope creep.",
              },
              {
                title: 'Proven Methodology',
                description:
                  "These are refined, repeatable processes that we've delivered many times.",
              },
              {
                title: 'Clear Deliverables',
                description:
                  "You know exactly what you'll receive and when. No ambiguity, no disappointment.",
              },
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              How Package Engagements Work
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A straightforward process from initial discussion to final delivery
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery Call',
                description: 'We discuss your needs and confirm the package is the right fit.',
              },
              {
                step: '02',
                title: 'Proposal',
                description: 'Fixed-price proposal with clear scope, timeline, and deliverables.',
              },
              {
                step: '03',
                title: 'Delivery',
                description: 'We execute the engagement, keeping you informed throughout.',
              },
              {
                step: '04',
                title: 'Handover',
                description: 'Final deliverables, presentation, and knowledge transfer.',
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="text-5xl font-heading font-bold text-teal/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Engagements */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Need Something Bespoke?
            </h2>
            <p className="text-muted-foreground mb-8">
              These packages cover common scenarios, but every organisation is unique. If you have
              specific requirements or need a custom engagement, we can create a tailored proposal.
            </p>
            <Link to="/services" className="btn-secondary-custom">
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a call to discuss your needs and see if one of our fixed-price packages is right
            for your organisation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
